import type { StudentCompetitionHistoryRow, StudentProfileFull, YearInSchool } from "../types/database";
import { supabase } from "./supabase";

function isYearInSchool(v: unknown): v is YearInSchool {
  return (
    v === "freshman" ||
    v === "sophomore" ||
    v === "junior" ||
    v === "senior" ||
    v === "graduate"
  );
}

export async function getStudentProfile(studentId: string): Promise<StudentProfileFull | null> {
  // Goes through a SECURITY DEFINER RPC — direct SELECTs on `students`
  // are blocked for peers by RLS (self-read only). The RPC returns rows
  // only when the caller is on a team with this student, has a pending
  // join request from them, is the same user, or is admin.
  const { data, error } = await supabase
    .rpc("get_student_public_profile", { p_student_id: studentId })
    .maybeSingle();

  if (error || !data) return null;
  const r = data as Record<string, unknown>;
  const yis = r.year_in_school;
  return {
    id: studentId,
    full_name: (r.full_name as string | null | undefined) ?? null,
    avatar_url: (r.avatar_url as string | null | undefined) ?? null,
    email: (r.email as string | null | undefined) ?? null,
    phone: (r.phone as string | null | undefined) ?? null,
    university: (r.university as string | null | undefined) ?? null,
    major: (r.major as string | null | undefined) ?? null,
    graduation_year: typeof r.graduation_year === "number" ? (r.graduation_year as number) : null,
    graduation_term: (r.graduation_term as string | null | undefined) ?? null,
    program_level: (r.program_level as string | null | undefined) ?? null,
    year_in_school: isYearInSchool(yis) ? yis : null,
    fields_of_competency: Array.isArray(r.fields_of_competency) ? (r.fields_of_competency as string[]) : null,
    linkedin_url: (r.linkedin_url as string | null | undefined) ?? null,
    resume_url: (r.resume_url as string | null | undefined) ?? null,
    bio: (r.bio as string | null | undefined) ?? null,
    state: (r.state as string | null | undefined) ?? null,
    city: (r.city as string | null | undefined) ?? null,
  };
}

export async function getStudentHistory(studentId: string): Promise<StudentCompetitionHistoryRow[]> {
  const { data, error } = await supabase.rpc("get_student_competition_history", {
    p_student_id: studentId,
  });

  if (error || !data || !Array.isArray(data)) return [];
  return data as StudentCompetitionHistoryRow[];
}

/** Signed URL for `profile-assets` object path, or pass-through for absolute URLs. */
export async function getSignedProfileAssetUrl(
  pathOrUrl: string | null
): Promise<{ url: string | null; error: Error | null }> {
  if (!pathOrUrl) return { url: null, error: null };
  if (/^https?:\/\//i.test(pathOrUrl)) {
    return { url: pathOrUrl, error: null };
  }
  const { data, error } = await supabase.storage.from("profile-assets").createSignedUrl(pathOrUrl, 3600);
  if (error) return { url: null, error: new Error(error.message) };
  return { url: data?.signedUrl ?? null, error: null };
}
